import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert, Paper, Box } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { TaskDraftDTO } from '../types/TaskDraftDTO';
import { TaskDecompositionRequestDTO } from '../types/TaskDecompositionRequestDTO';
import { DecompositionItemDTO } from '../types/DecompositionItemDTO';
import { UserDTO } from '../types';
import { adminService } from '../services/adminService';
import { taskService } from '../services/taskService';
import { mediaService } from '../services/mediaService';
import { Media } from '../types/media';

interface TaskDecompositionModalProps {
    open: boolean;
    onClose: () => void;
    task: TaskDraftDTO | null; // Изменяем тип на TaskDraftDTO | null
    onDecomposeSuccess: () => void;
}

const TaskDecompositionModal: React.FC<TaskDecompositionModalProps> = ({ open, onClose, task, onDecomposeSuccess }) => {
    const [availableUsers, setAvailableUsers] = useState<UserDTO[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]); // Изменено с draftSettings
    const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
    const [usersError, setUsersError] = useState<string | null>(null);
    const [decomposing, setDecomposing] = useState<boolean>(false);
    const [decomposeError, setDecomposeError] = useState<string | null>(null);

    // Новые состояния для полей TaskEntity
    const [newTitle, setNewTitle] = useState<string>('');
    const [newDescription, setNewDescription] = useState<string>('');
    const [newPriceInKopecks, setNewPriceInKopecks] = useState<string>('');
    const [newDeadline, setNewDeadline] = useState<string>('');
    const [newEstimatedHours, setNewEstimatedHours] = useState<string>('');
    const [newPriority, setNewPriority] = useState<string>('');
    const [newRequirements, setNewRequirements] = useState<string>('');
    const [newNeededUsers, setNewNeededUsers] = useState<string>(''); // Новое состояние для NeededUsers
    // NeededUsers будет рассчитан из количества выбранных исполнителей
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // Новое состояние для выбранных файлов
    // const [uploadingFiles, setUploadingFiles] = useState<boolean>(false); // Состояние для отслеживания загрузки файлов
    const [existingMedia, setExistingMedia] = useState<Media[]>([]); // Состояние для отображения существующих медиафайлов

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingUsers(true);
                const users = await adminService.getAllUsers();
                setAvailableUsers(users);
            } catch (err) {
                setUsersError('Не удалось загрузить список пользователей.');
                console.error(err);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        if (open && task) {
            setSelectedUserIds([]); // Сбрасываем выбранных пользователей
            setNewTitle(`${task?.title || ''}`);
            setNewDescription(task?.description || '');

            const initialTaskData = task; // Используем данные из TaskDraftDTO

            setNewPriceInKopecks(initialTaskData?.priceInKopecks?.toString() || '0');
            setNewDeadline(initialTaskData?.deadline ? initialTaskData.deadline.substring(0, 16) : '');
            setNewEstimatedHours(initialTaskData?.estimatedHours?.toString() || '');
            setNewPriority(initialTaskData?.priority?.toString() || '3');
            setNewRequirements(initialTaskData?.requirements || '');
            setNewNeededUsers(initialTaskData?.neededUsers?.toString() || ''); // Инициализируем NeededUsers

            setExistingMedia(task.media || []); // Инициализируем существующие медиафайлы
        } else if (!open) {
            // Сброс всех состояний при закрытии модального окна
            setSelectedUserIds([]);
            setNewTitle('');
            setNewDescription('');
            setNewPriceInKopecks('');
            setNewDeadline('');
            setNewEstimatedHours('');
            setNewPriority('');
            setNewRequirements('');
            setNewNeededUsers(''); // Сбрасываем NeededUsers
            setDecomposeError(null);
            setSelectedFiles([]); // Сбрасываем выбранные файлы
            setExistingMedia([]); // Сбрасываем существующие медиафайлы
        }
    }, [open, task]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setSelectedFiles(Array.from(event.target.files));
        }
    };

    const handleUserSelectChange = (e: SelectChangeEvent<string[]>) => {
        setSelectedUserIds(e.target.value as string[]);
    };

    const handleDecomposeConfirm = async () => {
        if (!task || selectedUserIds.length === 0) {
            setDecomposeError('Пожалуйста, выберите черновик задачи и исполнителей.');
            return;
        }

        if (!newTitle.trim()) {
            setDecomposeError('Название задачи не может быть пустым.');
            return;
        }
        if (isNaN(parseInt(newPriceInKopecks))) {
            setDecomposeError('Стоимость должна быть числом.');
            return;
        }
        if (isNaN(parseInt(newPriority)) || parseInt(newPriority) < 1 || parseInt(newPriority) > 5) {
            setDecomposeError('Приоритет должен быть числом от 1 до 5.');
            return;
        }

        const decompositionItems: DecompositionItemDTO[] = selectedUserIds.map(userId => ({
            assignedToUserId: userId,
        }));

        const request: TaskDecompositionRequestDTO = {
            originalTaskDraftId: task.id!,
            title: newTitle,
            description: newDescription,
            priceInKopecks: parseInt(newPriceInKopecks),
            deadline: newDeadline || undefined,
            estimatedHours: newEstimatedHours ? parseInt(newEstimatedHours) : undefined,
            priority: parseInt(newPriority),
            requirements: newRequirements || undefined,
            neededUsers: newNeededUsers ? parseInt(newNeededUsers) : undefined, // Включаем NeededUsers
            decompositionItems: decompositionItems,
        };

        setDecomposing(true);
        setDecomposeError(null);

        // setUploadingFiles(true); // Устанавливаем в true в начале
        try {
            const newTasks = await taskService.decomposeTask(request);

            if (selectedFiles.length > 0) {
                const uploadPromises: Promise<Media>[] = [];
                for (const task of newTasks) {
                    for (const file of selectedFiles) {
                        uploadPromises.push(mediaService.uploadFile(file, undefined, task.id)); // Привязываем к каждому новому TaskEntity
                    }
                }
                await Promise.all(uploadPromises);
            }

            onDecomposeSuccess();
            onClose();
        } catch (err) {
            setDecomposeError(`Ошибка при декомпозиции задачи: ${err instanceof Error ? err.message : String(err)}`);
            console.error(err);
        } finally {
            setDecomposing(false);
            // setUploadingFiles(false);
        }
    };

    if (!task) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Декомпозировать черновик задачи: {task.title}</DialogTitle>
            <DialogContent>
                {decomposeError && <Alert severity="error" sx={{ mb: 2 }}>{decomposeError}</Alert>}
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Исходный черновик задачи: {task.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {task.description}
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Детали новой задачи (TaskEntity)</Typography>
                <TextField
                    label="Название задачи"
                    fullWidth
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Описание задачи"
                    fullWidth
                    multiline
                    rows={3}
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Стоимость (в копейках)"
                    fullWidth
                    type="number"
                    value={newPriceInKopecks}
                    onChange={(e) => setNewPriceInKopecks(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Крайний срок"
                    fullWidth
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={newDeadline}
                    onChange={(e) => setNewDeadline(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Оценочные Часы"
                    fullWidth
                    type="number"
                    value={newEstimatedHours}
                    onChange={(e) => setNewEstimatedHours(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Приоритет (1-5)"
                    fullWidth
                    type="number"
                    inputProps={{ min: 1, max: 5 }}
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Требования"
                    fullWidth
                    multiline
                    rows={3}
                    value={newRequirements}
                    onChange={(e) => setNewRequirements(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Необходимые Исполнители (общее кол-во)"
                    fullWidth
                    type="number"
                    inputProps={{ min: 1 }}
                    value={newNeededUsers}
                    onChange={(e) => setNewNeededUsers(e.target.value)}
                    sx={{ mb: 2 }}
                />

                {existingMedia.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Уже прикрепленные файлы:</Typography>
                        {existingMedia.map((mediaItem) => (
                            <Paper key={mediaItem.id} variant="outlined" sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>{mediaItem.name}</Typography>
                                {/* Тут можно добавить кнопку для удаления файла, если это требуется */}
                            </Paper>
                        ))}
                    </Box>
                )}

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Прикрепить Медиафайлы (к каждой новой задаче)</Typography>
                <Box sx={{ mb: 3 }}>
                    <input
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        id="decompose-upload-media-button"
                        multiple
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="decompose-upload-media-button">
                        <Button variant="contained" component="span">
                            Выбрать Файлы
                        </Button>
                    </label>
                    {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">Выбрано файлов: {selectedFiles.length}</Typography>
                            <Button variant="outlined" onClick={() => setSelectedFiles([])} sx={{ ml: 1 }}>
                                Очистить Выбор
                            </Button>
                        </Box>
                    )}
                </Box>

                {loadingUsers ? (
                    <CircularProgress sx={{ mt: 3 }} />
                ) : usersError ? (
                    <Alert severity="error" sx={{ mt: 3 }}>{usersError}</Alert>
                ) : (
                    <FormControl fullWidth sx={{ mb: 3, mt: 3 }}>
                        <InputLabel>Выбрать исполнителей</InputLabel>
                        <Select
                            multiple
                            value={selectedUserIds}
                            onChange={handleUserSelectChange}
                            label="Выбрать исполнителей"
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((userId) => (
                                        <Paper key={userId} sx={{ p: '4px 8px', borderRadius: '4px', backgroundColor: (theme) => theme.palette.primary.light }}>
                                            <Typography variant="body2">
                                                {availableUsers.find(u => u.id === userId)?.personalData?.fullName || `Пользователь ${availableUsers.find(u => u.id === userId)?.phone}`}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        >
                            {availableUsers.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.personalData?.fullName
                                        ? user.personalData.fullName
                                        : `Пользователь ${user.phone}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={decomposing}>Отмена</Button>
                <Button onClick={handleDecomposeConfirm} color="primary" variant="contained" disabled={decomposing || selectedUserIds.length === 0}>
                    {decomposing ? <CircularProgress size={24} /> : 'Декомпозировать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskDecompositionModal;
